import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button, Text, Divider } from 'rsuite';
import dayjs from 'dayjs';
import pluralize from 'pluralize';
import {
  getTaxPaymentPeriodsAsync,
  loadLicensesForPeriod,
} from '../../store/slices/tax/taxThunks';
import AddAdditionalPeriodsModal from '../../components/tax/AddAdditionalPeriodsModal';
import { selectTaxState } from '../../store/slices/tax/taxSelectors';
import IconIndicator from '../../components/user-feedback/IconIndicator';
import Notice from '../../components/user-feedback/Notice';
import {
  clearPreSelectedTaxPeriod,
  resetTaxState,
  updateTaxFlowData,
} from '../../store/slices/tax/taxSlice';
import { C_TOT_PAYMENT_STATUS_INCOMPLETE } from '../../common/consts';
import { FLOWS_ENUM } from '../../common/enums';
import {
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigTaxPaymentPeriod,
  selectNumberOfProperties,
  selectBulkTax,
} from '../../store/slices/config/configSelectors';
import BasicStyledCheckbox from '../../components/form/BasicStyledCheckbox';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { useAppDispatch } from 'src/store/store';
import {
  ExtendedPaymentPeriod,
  PaymentPeriod,
  PeriodLicense,
  ReportingMethod,
} from '../../types/tax';
import { TaxLicensesTable } from '../../components/tax/TaxLicensesTable';
import SegmentedControlButtons from '../../components/common/SegmentedControlButtons';
import FlowLayout from 'src/components/layout/FlowLayout';

export const segmentedControlButtonOptions = [
  {
    value: ReportingMethod.INDIVIDUAL,
    label: 'Individually',
  },
  {
    value: ReportingMethod.BULK,
    label: 'Bulk Upload (Spreadsheet)',
  },
];

const TaxSelectProperties = () => {
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();

  const [additionalPeriodsModalOpen, setAdditionalPeriodsModalOpen] =
    useState(false);
  const [showNoSelectedLicenseError, setShowNoSelectedLicenseError] =
    useState(false);
  const [hideInactivePermits, setHideInactivePermits] = useState(true);

  const taxPaymentPeriod = useSelector(selectConfigTaxPaymentPeriod);

  const permitType = useSelector(selectConfigExpressRegistrationTypeVerbiage);
  const numberOfProperties = useSelector(selectNumberOfProperties);

  const isBulkTax = useSelector(selectBulkTax);

  const { taxFlowData: formData, preSelectedPeriod } =
    useSelector(selectTaxState);

  const [selectedLicenses, setSelectedLicenses] = useState<
    Record<string, Set<string>>
  >({});

  const {
    taxPaymentPeriods: {
      data: paymentPeriods,
      loading: paymentPeriodsLoading,
      error: paymentPeriodsError,
    },
    licensesPerPeriod,
  } = useSelector(selectTaxState);

  const [periodsToShow, setPeriodsToShow] = useState<
    Array<ExtendedPaymentPeriod>
  >([]);

  useEffect(() => {
    setPeriodsToShow(
      paymentPeriods.filter(
        (period) =>
          period.selected || preSelectedPeriod?.startDate === period.startDate
      )
    );

    if (preSelectedPeriod?.startDate) {
      dispatch(loadLicensesForPeriod(preSelectedPeriod as PaymentPeriod));
    }
  }, [paymentPeriods, preSelectedPeriod]);

  useEffect(() => {
    if (paymentPeriods.length === 0) {
      dispatch(getTaxPaymentPeriodsAsync());
    }
  }, [dispatch]);

  useEffect(() => {
    // This is used to set the initial state of the component when the user
    // has gone to the next page and then comes back to this page.
    const initialTrigger = () => {
      if (formData.selectedLicenses.length > 0) {
        setSelectedLicenses(
          formData.selectedLicenses.reduce((prev, period) => {
            return {
              ...prev,
              [period.startDate]: new Set(
                period.licenses.map((license) => license.license)
              ),
            };
          }, {})
        );

        const selectedPeriods = formData.selectedLicenses.map(
          (period) => period.startDate
        );
        setPeriodsToShow(
          paymentPeriods.filter((period) =>
            selectedPeriods.includes(period.startDate)
          )
        );
      }
    };
    initialTrigger();
  }, [formData]);

  const isBulkTaxCapable = useMemo(() => {
    if (!isBulkTax) {
      return false;
    }
    // Count the number of different licenses available
    const licenses = paymentPeriods.flatMap((period) => period.licenses);
    const licensesAvailable = [
      ...new Set(licenses.map((license) => license.license)),
    ];
    return licensesAvailable.length >= numberOfProperties;
  }, [numberOfProperties, isBulkTax, paymentPeriods]);

  const [reportingMethod, setReportingMethod] = useState<ReportingMethod>();

  const toggleSelectableTaxablePeriods = (period: ExtendedPaymentPeriod) => {
    const updatedReportingPeriods = [...paymentPeriods];
    const existingPeriod = updatedReportingPeriods?.find(
      (item) =>
        item.startDate === period.startDate && item.endDate === period.endDate
    );

    if (existingPeriod) {
      setPeriodsToShow((prev) => {
        const foundIndex = prev.findIndex(
          (period) => period.id === existingPeriod.id
        );

        if (foundIndex === -1) {
          return [
            ...prev,
            {
              ...period,
              licenses: preSelectLicensesInPeriod(existingPeriod),
            },
          ].sort((a, b) =>
            dayjs(b?.startDate).isAfter(dayjs(a?.startDate)) ? 1 : -1
          );
        }

        const toRemove = [...prev];
        toRemove.splice(foundIndex, 1);

        return toRemove;
      });

      dispatch(loadLicensesForPeriod(period));
    }
  };

  const preSelectLicensesInPeriod = (period: PaymentPeriod) => {
    return period.licenses.map((license) => ({
      ...license,
      selected: license.totPaidStatus === C_TOT_PAYMENT_STATUS_INCOMPLETE,
    }));
  };

  const toggleSelectedLicenseForPeriod = useCallback(
    (licenseNum: string, period: PaymentPeriod) => {
      const existingLicenses = licensesPerPeriod[period.startDate] ?? null;
      if (existingLicenses) {
        const licenseIndex = existingLicenses.data.findIndex(
          (license) => license.license === licenseNum
        );

        const existingLicense =
          licenseIndex !== -1 ? existingLicenses.data[licenseIndex] : null;
        if (existingLicense) {
          setSelectedLicenses((prev) => {
            const newSelectedLicenses =
              prev[period.startDate] ?? new Set<PeriodLicense>();
            if (prev[period.startDate]?.has(existingLicense.license ?? '')) {
              newSelectedLicenses.delete(existingLicense.license ?? '');
            } else {
              newSelectedLicenses.add(existingLicense.license ?? '');
            }
            return { ...prev, [period.startDate]: newSelectedLicenses };
          });
        }

        setShowNoSelectedLicenseError(false);
      }
    },
    [licensesPerPeriod]
  );

  const toggleSelectAllLicensesForPeriod = useCallback(
    (period: PaymentPeriod, isSelected: boolean) => {
      const existingLicenses = licensesPerPeriod[period.startDate] ?? null;
      if (existingLicenses && isSelected) {
        const selectedLicenses = new Set<string>(
          existingLicenses.data.map((lic) => lic.license ?? '')
        );
        setSelectedLicenses((prev) => ({
          ...prev,
          [period.startDate]: selectedLicenses,
        }));
      } else {
        setSelectedLicenses((prev) => ({
          ...prev,
          [period.startDate]: new Set<string>(),
        }));
      }
    },
    [licensesPerPeriod]
  );

  const handleNextClick = (): Promise<boolean> => {
    const entries = Object.entries(selectedLicenses);
    const periodsWithSelectedLicenses = entries.filter(
      ([, licenses]) => licenses.size > 0
    );
    const paymentPeriodsWithSelectedLicenses = paymentPeriods.filter((period) =>
      periodsWithSelectedLicenses.some(
        ([periodStart]) => periodStart === period.startDate
      )
    );
    const selectedData = paymentPeriodsWithSelectedLicenses.map(
      (period: ExtendedPaymentPeriod) => {
        const licenses = Array.from(selectedLicenses[period.startDate]).map(
          (licenseId) => {
            // Select the license that already has some reporting data in it
            // so that the form in report revenue is filled with the data the user
            // already provided.
            if (formData.selectedLicenses) {
              const formDataPeriod = formData.selectedLicenses.find(
                (selectedPeriod) =>
                  selectedPeriod.startDate === period.startDate
              );
              if (formDataPeriod) {
                const formDataLicense = formDataPeriod.licenses.find(
                  (selectedLicense) => selectedLicense.license === licenseId
                );

                if (formDataLicense) {
                  return formDataLicense;
                }
              }
            }

            return licensesPerPeriod[period.startDate].data.find(
              (license) => license.license === licenseId
            );
          }
        );
        return {
          ...period,
          licenses,
        };
      }
    );

    const hasSelectedLicenses = periodsWithSelectedLicenses.length > 0;

    if (hasSelectedLicenses) {
      if (!reportingMethod) {
        setShowNoSelectedLicenseError(true);
        return Promise.resolve(false);
      }

      dispatch(clearPreSelectedTaxPeriod());
      dispatch(
        updateTaxFlowData({
          selectedLicenses: selectedData,
          reportingMethod,
        })
      );
      return Promise.resolve(true);
    }
    setShowNoSelectedLicenseError(true);
    return Promise.resolve(false);
  };

  const hasSelectedLicenses = useMemo(() => {
    return Object.values(selectedLicenses).some(
      (licenses) => licenses.size > 0
    );
  }, [selectedLicenses]);

  const handleBackClick = (): Promise<boolean> => {
    dispatch(resetTaxState());
    placeNavigate('/dashboard');
    return Promise.resolve(true);
  };

  useEffect(() => {
    setReportingMethod(
      isBulkTaxCapable ? formData.reportingMethod : ReportingMethod.INDIVIDUAL
    );
  }, [formData.reportingMethod, isBulkTaxCapable, selectedLicenses]);

  return (
    <FlowLayout
      flow={FLOWS_ENUM.PAY_TOT}
      onBack={handleBackClick}
      onNext={handleNextClick}
      omitNavigationForward={!reportingMethod || !hasSelectedLicenses}
      showBack
      showNext
      title="Select Properties for Tax Reporting"
    >
      <Text className="mb-24">
        Your properties that are registered for tax reporting and remittance in
        the selected period are listed below. Please select the properties for
        which you wish to report at this time.
      </Text>
      {paymentPeriodsLoading && <IconIndicator />}
      {!paymentPeriodsLoading && (
        <>
          {periodsToShow?.map((period) => (
            <div
              className="mb-24"
              key={`tax_period_${period.startDate}_${period.endDate}`}
            >
              <Text className="text-bold  header-text-h4 mb-16">
                {period.name}
              </Text>
              <TaxLicensesTable
                period={period}
                permitType={permitType}
                selectAll={toggleSelectAllLicensesForPeriod}
                selectLicense={toggleSelectedLicenseForPeriod}
                licenses={licensesPerPeriod[period.startDate]?.data || []}
                selectedLicenses={selectedLicenses[period.startDate]}
                loading={licensesPerPeriod[period.startDate]?.loading}
              />
            </div>
          ))}
          {!periodsToShow.length && (
            <Notice
              message="No Payment periods Selected"
              type="info"
            />
          )}

          {paymentPeriodsError && (
            <Notice
              style={{ marginBottom: '15px' }}
              message={paymentPeriodsError}
            />
          )}
          <div className="responsive-stack">
            <Button
              className="btn-tertiary sm-100-percent"
              onClick={() => setAdditionalPeriodsModalOpen(true)}
            >
              Add Additional Periods
            </Button>

            <BasicStyledCheckbox
              onChange={(checked: boolean) => {
                setHideInactivePermits(checked);
              }}
              checked={hideInactivePermits}
              label={`Hide Inactive ${pluralize(permitType)}`}
            />
          </div>
          {isBulkTaxCapable && (
            <>
              <Divider />
              <Text className="text-large text-bold mb-12">
                How would you like to report tax for the selected properties?
              </Text>
              <Text className="mb-16">
                For tax reporting of {numberOfProperties} or more properties,
                you can report individually, or upload in bulk using a
                spreadsheet template on the next screen.
              </Text>
              <SegmentedControlButtons
                value={reportingMethod}
                setValue={setReportingMethod}
                options={segmentedControlButtonOptions}
              />
            </>
          )}
          {showNoSelectedLicenseError && (
            <Notice
              message={
                !reportingMethod
                  ? 'Please select a reporting method to continue'
                  : 'Please select one or more licenses to continue'
              }
              className="mt-12"
            />
          )}
        </>
      )}
      <AddAdditionalPeriodsModal
        availableReportingPeriods={paymentPeriods}
        isOpen={additionalPeriodsModalOpen}
        onSelect={toggleSelectableTaxablePeriods}
        selectedPeriods={periodsToShow}
        setOpen={setAdditionalPeriodsModalOpen}
        taxPaymentPeriod={taxPaymentPeriod}
      />
    </FlowLayout>
  );
};

export default TaxSelectProperties;
